import { FilterItemSource } from '@/infra/FiltersViewModel'
import { action, computed, when } from 'mobx'
import { type SelectFilter } from '@/infra/SelectFilter'
import type { Id } from '@/infra/id'
import { type Workspace } from '@/features/iam/workspaces/Workspace'
import { type JobManagementViewModel } from '@/features/jobs/list/JobManagementViewModel'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'

/**
 * Lists one user's jobs on their user details page.
 */
export class UserJobsTabViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  constructor(
    private readonly _getUserPublicId: () => Id,
    private readonly _getWorkspaces: () => Workspace[],
    readonly jobManagementVm: JobManagementViewModel,
  ) {
    this._lifecycle.manage(jobManagementVm)
  }

  /**
   * Returns the current selected user
   */
  @computed
  get userPublicId() {
    return this._getUserPublicId()
  }

  /**
   * Returns member filter
   */
  @computed
  get memberFilter() {
    return this.jobManagementVm.searcher.filters.getFilter(
      'members',
    ) as SelectFilter<string, Id>
  }

  /**
   * Returns workspaces filter
   */
  @computed
  get workspacesFilter() {
    return this.jobManagementVm.searcher.filters.getFilter(
      'workspaces',
    ) as SelectFilter<string, Id>
  }

  /**
   * Whether a search or filter narrows the list.
   */
  @computed
  get isSearchFilterApplied() {
    const isSearching = this.jobManagementVm.searcher.searchText.value !== ''
    const haveFiltersApplied =
      this.jobManagementVm.searcher.filters.appliedFilterCount > 0

    return isSearching || haveFiltersApplied
  }

  /**
   * Whether every page loaded and the user has no matching jobs.
   */
  @computed
  get shouldShowEmptyState() {
    return (
      this.jobManagementVm.searcher.scroller.isDone &&
      this.jobManagementVm.searcher.jobs.length === 0
    )
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    // The view renders a failed load with the job management task.
    this._applyUserFilters().catch(() => undefined)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Clears any search filters.
   */
  @action.bound
  clearFilters() {
    this.jobManagementVm.searcher.clearFilters()
  }

  private async _applyUserFilters() {
    // Loading restores the saved filters, which would replace this user's.
    await when(
      () => !this.jobManagementVm.fetch.pending || !this._lifecycle.active,
    )
    if (!this._lifecycle.active || this.jobManagementVm.fetch.rejected) {
      return
    }

    this.memberFilter.change([this.userPublicId])

    // because we add the current logged user selected workspace from the workspace switcher sidebar as default,
    // we need to clear the filter selected value
    this.workspacesFilter.clearValue()
    // Activating the filters recreates them with the current user's
    // workspaces as the source.
    this.workspacesFilter.itemSource = FilterItemSource.static(
      this._getWorkspaces().map((w) => ({
        title: w.name,
        value: w.id,
      })),
    )
    await this.workspacesFilter.fetchOptions()
  }
}
